import React, { useEffect } from 'react';
import { View, ImageBackground, Image } from 'react-native';
import Typography from 'src/components/Text/Text';
import { styles } from './QuotesSuccessScreen.styles';
import { trackGTMEvent } from 'src/helpers/gtm/gtmEvents';

import background from 'src/assets/bg.png';
import ribbon from 'src/assets/ribbons.png';
import confetti from 'src/assets/confetti.gif';
import carousal from 'src/assets/carouselV2.gif';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'src/store/store';
import { useGetCompanyQuery } from 'src/features/company/api/companyApi';
import { FLOW_STEP } from 'src/constants/company';
import { setShowGlobalLoader } from 'src/slices/UiSlice';
import QuoteSummaryCard from 'src/features/quotes/components/QuoteSummaryCard';

const QuoteSuccessScreen: React.FC = () => {
  const currentCompany = useSelector((state: RootState) => state.company.company);
  const accountType = useSelector((state: RootState) => state.UI.accountType);

  const dispatch = useDispatch();

  const { data: company, isLoading } = useGetCompanyQuery(currentCompany?.id || 0, {
    skip: !currentCompany?.id,
    refetchOnFocus: true,
    refetchOnMountOrArgChange: true,
  });
  const quoteStatus = company?.flowStep;

  const lastQuoteScreen = useSelector((state: RootState) => state.UI.lastQuoteScreen);
  const { selectedQuoteId, quotes } = useSelector((state: RootState) => state.quotes);

  const selectedQuote = quotes.find((quote) => String(quote.id) === selectedQuoteId);

  useEffect(() => {
    if (!isLoading) {
      dispatch(setShowGlobalLoader(false));
    }
  }, [isLoading, dispatch]);

  useEffect(() => {
    trackGTMEvent(
      'success-screen-landed',
      {
        userEvent: 'User landed on the Success screen',
      },
      accountType,
    );
  }, []);

  return (
    <View style={styles.container} testID={'quote-success-screen'}>
      {/* Background Image */}
      <ImageBackground
        source={background}
        style={styles.backgroundImage}
        imageStyle={styles.imageStyle}>
        {/* Ribbon Background (Layered on top of the main background) */}
        <Image source={ribbon} style={styles.ribbonImage} />

        {lastQuoteScreen === 'PURCHASE' ? (
          <>
            {/* Confetti Image (Layered on top of everything) */}
            <View style={styles.confettiContainer}>
              <Image source={confetti} style={styles.confettiImage} />
            </View>

            {/* Title Text */}
            <View style={styles.textContainer}>
              <Typography variant="h1" style={styles.titleText}>
                Order placed successfully! One of our agents will reach out to you to collect
                payment and share policy documentation
              </Typography>
            </View>

            {/* Quote Details Section */}
            {selectedQuote && (
              <View style={styles.quoteCardContainer}>
                <QuoteSummaryCard
                  coverages={selectedQuote.linesOfBusiness}
                  premiumAmount={selectedQuote.totalPremiumWEndorsements.amount}
                />
              </View>
            )}
          </>
        ) : (
          <>
            {/* Confetti Image (Layered on top of everything) */}
            <View style={styles.confettiContainer}>
              <Image source={confetti} style={styles.confettiImage} />
            </View>

            {/* Title Text */}
            <View style={styles.textContainer}>
              <Typography variant="h1" style={styles.titleText}>
                {quoteStatus === FLOW_STEP.WAITING
                  ? 'Thank you for sharing your details!'
                  : 'Hooray!! We have generated 1 instant quote for you.'}
              </Typography>
              <Typography variant="title1" style={styles.subText}>
                {quoteStatus === FLOW_STEP.WAITING
                  ? 'Due to high demand, we are forced to prioritize serving users needing policies live within 90 days.'
                  : '3 more are on the way!'}
              </Typography>
            </View>

            {/* Carousal Card animation */}
            <View style={styles.cardCarousalContainer}>
              <Image source={carousal} style={styles.caraousalImage} />
            </View>

            {/* Additional Text Under Carousel */}
            <View style={styles.cardCaptionContainer}>
              <Typography variant="body1" style={styles.cardCaptionText}>
                {quoteStatus === FLOW_STEP.WAITING
                  ? 'Our team will contact you soon with quotes and more details.'
                  : 'One of our producers will reach out to you shortly with the other quotes!'}
              </Typography>
            </View>
          </>
        )}
      </ImageBackground>
    </View>
  );
};

export default QuoteSuccessScreen;
